/**
 * Назва вкладки за маршрутом — з конфігурації меню (layouts/sidebarItems).
 * Точний збіг `to` → лейбл пункту; інакше найдовший префікс (редактори
 * документів живуть на /X/:id, а меню знає /X). Сторінка може уточнити
 * назву через useTabTitle (напр. «Бухгалтерська довідка № 81»).
 */
import { I18N_PREFIX, SIDEBAR_ITEMS } from '@/layouts/sidebarItems';

type T = (key: string) => string;

interface Entry { prefix: string; label: string }

let cache: Entry[] | null = null;

function entries(): Entry[] {
  if (cache) return cache;
  const out: Entry[] = [];
  for (const it of SIDEBAR_ITEMS) {
    if (it.type !== 'link') continue;
    out.push({ prefix: it.to, label: it.label });
    for (const m of it.match ?? []) out.push({ prefix: m, label: it.label });
  }
  // Довші префікси — першими (щоб /refs/contract-account-rules не з'їдався /refs).
  out.sort((a, b) => b.prefix.length - a.prefix.length);
  cache = out;
  return out;
}

function label(raw: string, t: T): string {
  return raw.startsWith(I18N_PREFIX) ? t(raw.slice(I18N_PREFIX.length)) : raw;
}

/** Людська назва сторінки для вкладки. */
export function resolveRouteTitle(pathname: string, t: T): string {
  const list = entries();
  const exact = list.find((e) => e.prefix === pathname);
  if (exact) return label(exact.label, t);

  const pref = list.find(
    (e) => e.prefix !== '/' && pathname.startsWith(`${e.prefix}/`));
  if (pref) {
    const base = label(pref.label, t);
    return pathname.endsWith('/new') ? `${base} — новий` : base;
  }
  if (pathname === '/') return label('nav.home', t) || 'Загальне';
  // Невідомий маршрут: останній сегмент людською мовою.
  const seg = pathname.split('/').filter(Boolean).pop() ?? '';
  return seg ? seg.replace(/-/g, ' ') : pathname;
}


/**
 * Чи це сторінка ДОКУМЕНТА/КАРТКИ (а не список із меню).
 *
 * Ознака: шлях НЕ збігається з пунктом меню (то список) І закінчується
 * ідентифікатором — uuid / число / «new».
 */
export function isDocumentRoute(pathname: string): boolean {
  if (entries().some((e) => e.prefix === pathname)) return false;
  const segs = pathname.split('/').filter(Boolean);
  if (segs.length < 2) return false;
  const last = segs[segs.length - 1] ?? '';
  return last === 'new'
    || /^\d+$/.test(last)
    || /^[0-9a-f]{8}-[0-9a-f-]{8,}$/i.test(last);
}

/** Чи це сторінка-СПИСОК із меню/субменю (точний збіг з пунктом меню). */
export function isMenuRoute(pathname: string): boolean {
  return entries().some((e) => e.prefix === pathname);
}

/**
 * Чи тримаємо сторінку у вкладках.
 * ч.950 (рішення власника): ТАК і для документів, і для сторінок меню/субменю
 * (раніше — лише документи). Списки теж потрапляють у смугу вкладок.
 */
export function isTabbableRoute(pathname: string): boolean {
  return isMenuRoute(pathname) || isDocumentRoute(pathname);
}
